import {
  MAX_LEN_WEB_S,
  MAX_PHOTO_BYTES,
  MAX_SONG_BYTES,
  MAX_SONG_DURATION_MS,
  PHOTO_EXTENSIONS,
  SONG_EXTENSIONS,
} from "@goalhorns/shared";
import type { MessageKey } from "../../i18n/en";
import { messageKeyForError } from "../../i18n/errors";
import { sha256Hex } from "../../lib/hash";
import { playerApi, type PlayerProfile, type SetSongPhase } from "./api";
import { clampRegion, type ClampedRegion } from "./region";
import { readDurationMs } from "./song-duration";

const MS_PER_S = 1000;

/** A problem found before any request is sent; the message is a translation key. */
export class PlayerInputError extends Error {
  readonly messageKey: MessageKey;

  constructor(messageKey: MessageKey) {
    super(messageKey);
    this.name = "PlayerInputError";
    this.messageKey = messageKey;
  }
}

/** Translation key for anything a player action can throw. */
export function errorMessageKey(error: unknown): MessageKey {
  return error instanceof PlayerInputError ? error.messageKey : messageKeyForError(error);
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

function checkFile(
  file: File,
  allowed: readonly string[],
  maxBytes: number,
  tooLargeKey: MessageKey = "error.too_large",
): string {
  const ext = fileExtension(file.name);
  if (!allowed.includes(ext)) {
    throw new PlayerInputError("error.ext_not_allowed");
  }
  if (file.size === 0) {
    throw new PlayerInputError("error.empty_body");
  }
  if (file.size > maxBytes) {
    throw new PlayerInputError(tooLargeKey);
  }
  return ext;
}

export interface PhotoUploadOptions {
  readonly hasPhoto: boolean;
  readonly consent: boolean;
  readonly onProgress: (fraction: number) => void;
}

/** Uploads a photo, then points the player at it. The first photo carries the consent flag. */
export async function uploadPhoto(
  token: string,
  file: File,
  options: PhotoUploadOptions,
): Promise<PlayerProfile> {
  const ext = checkFile(file, PHOTO_EXTENSIONS, MAX_PHOTO_BYTES);
  if (!options.hasPhoto && !options.consent) {
    throw new PlayerInputError("error.consent_required");
  }
  const sha256 = await sha256Hex(file);
  const { media } = await playerApi.upload(token, "photo", file, ext, sha256, options.onProgress);
  const patch = options.hasPhoto
    ? { photo_media_id: media.id }
    : { photo_media_id: media.id, consent: true };
  return playerApi.patch(token, patch);
}

/**
 * Uploads a song and sets its default region: start 0, length min(30 s, song length).
 * Only MP3, M4A and AAC are accepted; anything else is refused before any upload.
 */
export async function uploadSong(
  token: string,
  file: File,
  setPhase: SetSongPhase,
): Promise<PlayerProfile> {
  const ext = checkFile(file, SONG_EXTENSIONS, MAX_SONG_BYTES);
  setPhase({ kind: "prepare" });
  const durationMs = await readDurationMs(file);
  if (durationMs > MAX_SONG_DURATION_MS) {
    throw new PlayerInputError("error.too_large");
  }
  const initial = clampRegion(0, MAX_LEN_WEB_S * MS_PER_S, durationMs);
  if (!initial.ok) {
    throw new PlayerInputError("player.songTooShort");
  }
  const sha256 = await sha256Hex(file);
  const { media } = await playerApi.upload(
    token,
    "song",
    file,
    ext,
    sha256,
    (fraction) => setPhase({ kind: "upload", fraction }),
    durationMs,
  );
  return playerApi.patch(token, {
    song_media_id: media.id,
    start_ms: initial.start_ms,
    length_s: initial.length_ms / MS_PER_S,
  });
}

/** Saves the player's region. The length is whole seconds by construction. */
export function saveRegion(token: string, region: ClampedRegion): Promise<PlayerProfile> {
  return playerApi.patch(token, {
    start_ms: region.start_ms,
    length_s: region.length_ms / MS_PER_S,
  });
}
